/**
 * JAISIU-3656 — in-thread search deep-batch walker.
 *
 * The chat-thread search input filters the visible transcript to
 * messages that match the query. The visible transcript, however, is
 * NOT the full conversation — `chat.history` returns a paginated
 * window and the operator only sees the latest-N messages unless
 * they scroll up to trigger `loadOlderChatHistory`. A query that
 * only matches older, never-rendered messages would silently produce
 * zero hits even when the answer is in the same chat.
 *
 * Earlier rounds (JAISIU-3655 + pre-fix wiring in
 * `app-render-fleet-chat.ts:413`) used `drainOlderChatHistory()` to
 * eagerly load EVERY older page as soon as the operator typed. That
 * had two problems this ticket is fixing:
 *
 *   1. It mutated the live transcript (`chatMessagesBySession`) —
 *      side effects visible in the rendered window even when the
 *      operator never navigated into the older pages. Acceptance
 *      criterion #2 is "Rendering side effects are limited to
 *      batches the user actually navigates into."
 *
 *   2. It loaded the full transcript on every keystroke. Even a
 *      query with no matches at all triggered a worst-case 10k page
 *      drain in the background. Wasteful and noisy on the wire.
 *
 * This module replaces the eager drain with a non-mutating search
 * walker:
 *
 *   1. It scans the LIVE cache (the operator's currently rendered
 *      window) for matches first — no RPCs at all if the live slice
 *      alone has hits.
 *
 *   2. If the live slice has zero matches but the server still
 *      reports older content, it pages `chat.history` WITH
 *      `before=<oldestMessageId>` until either:
 *        - the cumulative match count hits `MAX_RESULTS`,
 *        - the server reports `hasMoreOlder: false`,
 *        - or we hit `MAX_PAGES`.
 *      Each page is BUILT into a per-session EPHEMERAL TEXT INDEX
 *      that lives only in module-scope state — never in the live
 *      cache, never in the DOM, never written to IndexedDB.
 *
 *   3. When the operator presses `↓` to navigate to a deep-batch
 *      match, the host calls `navigateToChatThreadDeepMatch(...)`,
 *      which checks whether the message id is already in the live
 *      cache; if not, it triggers `loadOlderChatHistory` UNTIL the
 *      id is rendered. THAT step is the only one that mutates
 *      `chatMessagesBySession`, and it only mutates the visible
 *      window to the precise page containing the target — never
 *      beyond.
 *
 * Critical invariants:
 *
 *   - **`chatMessagesBySession` is never written by the walker.**
 *     A reference to the live cache is snapshotted at the start of
 *     `searchActiveChatDeepBatch` and asserted unchanged afterwards.
 *     Tests lock this in (acceptance criterion #2).
 *
 *   - **Older pages do not render as a side effect of searching.**
 *     The walker accumulates text rows in module-scope state; only a
 *     subsequent explicit navigation triggers a `loadOlderChatHistory`
 *     call that prepends to the live cache (acceptance criterion #2).
 *
 *   - **Matches older than the live cache DO show up in the counter.**
 *     The counter reads the host's `chatMessageSearchTotal`, which
 *     the host recomputes from `live matches + deep matches`. Old
 *     hits are searchable, even though the row itself never paints
 *     until the operator navigates into it (acceptance criterion #1).
 *
 * This pattern is a strict subset of the global-search-deep-batch
 * walker (JAISIU-3652) but narrower: we walk ONE session (the
 * active one) and use the live cache's own cursor as the starting
 * point. The global walker is allowed to skip the active session
 * (the live cache already covers the live hits); the in-thread
 * walker IS the active session.
 */

// ------------------------------------------------------------------
// Imports
// ------------------------------------------------------------------

import { extractText } from "./message-extract.ts";

// ------------------------------------------------------------------
// Constants

// ------------------------------------------------------------------
// Constants
// ------------------------------------------------------------------

/** Maximum older pages walked per `chat-thread` search invocation.
 * Caps fanout so a mistyped query never triggers a 10k-page drain. */
export const MAX_PAGES = 6;

/** Page size requested from `chat.history` for the deep walker.
 * Matches the loader used by `loadOlderChatHistory` so the server's
 * cursor semantics align (the cursor is "oldest message id in the
 * previously returned page"). */
export const PAGE_SIZE = 200;

/** Maximum total deep-batch matches we record against the in-thread
 * counter. Mirrors the gateway's `maxResults` so the counter stays
 * bounded. Live-cache matches are NOT capped by this — they reflect
 * only what is currently rendered. */
export const MAX_DEEP_RESULTS = 200;

/** Minimum query length, mirroring `chat-thread-search-highlight.ts`. */
export const MIN_QUERY_LENGTH = 2;

// ------------------------------------------------------------------
// Types
// ------------------------------------------------------------------

/**
 * A single deep-batch match. Distinct from the global-search row
 * shape because the in-thread variant does not need session grouping,
 * gateway-side dedupe, or a `matchedText` preview (the operator
 * navigates by id, not by scanning a list).
 *
 * The `score` field is currently a constant 1 — reserved for future
 * ranking (recency, proximity to the operator's last-viewed message).
 */
export type DeepMatch = {
  /** Stable server-issued id of the matching message. */
  messageId: string;
  /** Role normalised to user/assistant/system/tool. */
  role: "user" | "assistant" | "system" | "tool" | "other";
  /** Best-effort createdAt in ms. */
  timestamp?: number;
  /** Excerpt around the first match in the message (≤120 chars). */
  excerpt: string;
};

/**
 * Result of a single `searchActiveChatDeepBatch` call.
 * `liveMatches` are matches found in `chatMessages` (the operator's
 * currently rendered window). `deepMatches` are matches found in
 * older pages — they are not currently rendered.
 */
export type DeepBatchSearchResult = {
  /** Session key the search ran against. */
  sessionKey: string;
  /** Trimmed query that produced these matches. */
  query: string;
  /** Matches in the live cache. */
  liveMatches: DeepMatch[];
  /** Matches in older, non-rendered pages. */
  deepMatches: DeepMatch[];
  /** Total matches capped at `MAX_DEEP_RESULTS + live size`. */
  total: number;
  /**
   * True if the walker terminated early because the server said
   * `hasMoreOlder: false`. False if the walker stopped due to any
   * other cap (`MAX_PAGES`, `MAX_DEEP_RESULTS`, error, no client).
   */
  exhausted: boolean;
};

// ------------------------------------------------------------------
// Source interface (the host subset the walker needs)
// ------------------------------------------------------------------

/** Minimal gateway capability the walker requires. */
export type DeepBatchGateway = {
  request<T = unknown>(method: string, params?: Record<string, unknown>): Promise<T>;
};

/**
 * Subset of the OpenClawApp surface the walker consumes. Narrower
 * than `DeepBatchSessionSource` in `global-search-deep-batch.ts`
 * because the in-thread walker is single-session; it does not need
 * `listSessions()` or the full `chatMessagesBySession` map (it
 * reads only `chatMessages`).
 */
/**
 * Subset of the OpenClawApp surface the walker consumes. Narrower
 * than `DeepBatchSessionSource` in `global-search-deep-batch.ts`
 * because the in-thread walker is single-session; it does not need
 * `listSessions()` or the full `chatMessagesBySession` map (it
 * reads only `chatMessages`).
 *
 * Declared as a structural interface (no `Pick<OpenClawApp, …>`) so
 * the test file does not have to drag in the full `OpenClawApp`
 * type — `OpenClawApp` satisfies the interface by duck-typing.
 */
export type ActiveChatSource = {
  sessionKey: string;
  chatMessages: unknown[] | undefined;
  chatHistoryOldestLoadedId: string | null | undefined;
  chatHistoryHasMoreOlder: boolean | undefined;
  chatHistoryLoadingOlder: boolean | undefined;
  connected: boolean;
  client: DeepBatchGateway | null | undefined;
};

// ------------------------------------------------------------------
// Per-session ephemeral state (module scope)
// ------------------------------------------------------------------

/**
 * One frozen page from a `chat.history` call. Stored so a
 * subsequent search in the same modal-thread session can re-use the
 * already-fetched pages and skip redundant RPCs.
 */
type DeepPage = {
  /** Server-issued id of the OLDEST message in this page. */
  oldestMessageId: string | null;
  /** True when the server says there is more to walk. */
  hasMoreOlder: boolean;
  /** Per-message rows (id + role + timestamp + lowercased text). */
  rows: DeepRow[];
};

/** One message's lowercased text + metadata. Built on page load. */
type DeepRow = {
  id: string;
  role: "user" | "assistant" | "system" | "tool" | "other";
  timestamp?: number;
  /** Original case text (kept for excerpt rendering — the
   *  case-insensitive matching uses `textLowercase`). */
  text: string;
  textLowercase: string;
};

/** Per-session cached walker state. Cleared on session switch. */
type DeepSessionCacheEntry = {
  sessionKey: string;
  /** Already-walked older pages (most recent first → oldest last). */
  pages: DeepPage[];
  /** True if the server has signalled end of older history. */
  exhausted: boolean;
  /**
   * Pinned cursor — what we'd use as `before=` on the NEXT page
   * request. Kept separate from `pages[-1].oldestMessageId` so the
   * walker can bump it past a known-empty page.
   */
  nextCursor: string | null;
};

const deepCache = new Map<string, DeepSessionCacheEntry>();

/** Generation counter — lets a newer search supersede an older
 * walker mid-flight. Mirrors the pattern in
 * `global-search-deep-batch.ts`. */
let deepSearchGeneration = 0;
/** True while a walker is currently paging older batches. */
let deepSearchInFlight = false;

// ------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------

/** Extract a stable id from a chat-history message, mirroring
 * `getMessageId` in `app-chat-cursor-parity.ts` but kept local so
 * the in-thread walker has no dependency on the chat controller. */
function getMessageId(message: unknown): string | undefined {
  if (!message || typeof message !== "object") return undefined;
  const record = message as {
    id?: unknown;
    transcriptId?: unknown;
    messageId?: unknown;
    message?: { id?: unknown };
  };
  for (const key of ["id", "transcriptId", "messageId"] as const) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  const inner = record.message;
  if (inner && typeof inner === "object") {
    const innerId = (inner as { id?: unknown }).id;
    if (typeof innerId === "string" && innerId.trim()) {
      return innerId.trim();
    }
  }
  return undefined;
}

function getRole(message: unknown): DeepRow["role"] {
  const role = (message as { role?: unknown }).role;
  if (role === "user" || role === "assistant" || role === "system" || role === "tool") {
    return role;
  }
  return "other";
}

function getTimestamp(message: unknown): number | undefined {
  const m = message as { createdAt?: unknown; timestamp?: unknown };
  for (const key of ["createdAt", "timestamp"] as const) {
    const raw = m[key];
    if (typeof raw === "number" && Number.isFinite(raw)) {
      return raw;
    }
    if (typeof raw === "string") {
      const parsed = Date.parse(raw);
      if (!Number.isNaN(parsed)) return parsed;
    }
  }
  return undefined;
}

/** Build a `DeepRow` from a chat-history message. Returns null when
 * the row has no usable id or empty text. */
function rowFromMessage(message: unknown): DeepRow | null {
  const id = getMessageId(message);
  if (!id) return null;
  const text = extractText(message) ?? "";
  if (!text.trim()) {
    // Still record the row (an empty message can have a match
    // later — metadata-only rows exist for tool calls) but with
    // empty text so the matcher ignores it. Return so the id is
    // known to callers that want to walk-by-id.
    return {
      id,
      role: getRole(message),
      timestamp: getTimestamp(message),
      text: "",
      textLowercase: "",
    };
  }
  return {
    id,
    role: getRole(message),
    timestamp: getTimestamp(message),
    text,
    textLowercase: text.toLowerCase(),
  };
}

/** Slice a 120-char excerpt around the first occurrence of `query`
 * in `text`. Mirrors the global walker's `buildMatchedText` so the
 * UI feels consistent. */
function buildExcerpt(text: string, query: string, maxLen = 120): string {
  if (!text) return "";
  const haystack = text.toLowerCase();
  const needle = query.toLowerCase();
  const idx = haystack.indexOf(needle);
  if (idx < 0) {
    return text.length > maxLen ? `…${text.slice(0, maxLen - 1)}` : text;
  }
  const half = Math.floor((maxLen - 1) / 2);
  const start = Math.max(0, idx - half);
  const end = Math.min(text.length, start + maxLen);
  const prefix = start > 0 ? "…" : "";
  const suffix = end < text.length ? "…" : "";
  return prefix + text.slice(start, end) + suffix;
}

/** Build a `DeepPage` from one `chat.history` response. */
function buildPage(response: { messages?: unknown[]; oldestMessageId?: unknown; hasMoreOlder?: unknown }): DeepPage {
  const messages = Array.isArray(response.messages) ? response.messages : [];
  const rows: DeepRow[] = [];
  for (const m of messages) {
    const row = rowFromMessage(m);
    if (row) rows.push(row);
  }
  const oldestMessageId =
    typeof response.oldestMessageId === "string" && response.oldestMessageId
      ? response.oldestMessageId
      : null;
  return {
    oldestMessageId,
    hasMoreOlder: response.hasMoreOlder === true,
    rows,
  };
}

/** Scan one page for matches. Returns a row per match — a single
 * message can yield more than one `DeepMatch` (one per occurrence of
 * the query inside its text) but in practice each row contributes at
 * most one match; the per-row loop is a safety net for any future
 * widening. */
function scanPageForMatches(page: DeepPage, query: string, limit: number): DeepMatch[] {
  if (limit <= 0) return [];
  const out: DeepMatch[] = [];
  // JAISIU-3655 — collapse internal whitespace on both sides.
  // Leading / trailing whitespace is preserved as a literal
  // substring requirement (see scanLiveForMatches for the same
  // contract); internal whitespace matches any whitespace run.
  const needle = query.toLowerCase().replace(/\s+/g, " ");
  for (const row of page.rows) {
    if (out.length >= limit) break;
    if (!row.textLowercase) continue;
    const haystack = row.textLowercase.replace(/\s+/g, " ");
    let cursor = 0;
    while (cursor < haystack.length) {
      const idx = haystack.indexOf(needle, cursor);
      if (idx < 0) break;
      out.push({
        messageId: row.id,
        role: row.role,
        timestamp: row.timestamp,
        excerpt: buildExcerpt(row.text, query),
      });
      cursor = idx + needle.length;
      if (out.length >= limit) break;
    }
  }
  return out;
}

/** Scan the live cache (the operator's currently rendered window)
 * for matches. This is the cheap path: zero RPCs. Exported for
 * unit tests. */
export function scanLiveForMatches(
  messages: readonly unknown[],
  query: string,
  limit: number,
): DeepMatch[] {
  if (limit <= 0 || !Array.isArray(messages)) return [];
  const out: DeepMatch[] = [];
  // JAISIU-3655 — collapse internal whitespace on BOTH sides so
  // a query whose only whitespace is interior ("sentence. The"
  // or "can  do") matches a row whose `extractText` joins parts
  // with "\n" (the deeper rows are joined with "\n" between
  // messages; `extractText` joins content parts with "\n"
  // internally). Leading / trailing whitespace is preserved as
  // a literal substring requirement — "can " with a trailing
  // space is NOT the same as "can".
  //
  // JAISIU-3655 (occurrence-level) — emit one `DeepMatch` per
  // substring occurrence inside the row (the previous contract
  // was one match per message, which made the counter disagree
  // with the visible highlight count whenever a message had
  // multiple occurrences). The host treats each `DeepMatch` as
  // one entry in `chatMessageSearchMatchIds` and as one `<mark>`
  // in the highlight pass; nav cycles them individually.
  const needle = query.toLowerCase().replace(/\s+/g, " ");
  for (const m of messages) {
    if (out.length >= limit) break;
    const row = rowFromMessage(m);
    if (!row || !row.textLowercase) continue;
    const haystack = row.textLowercase.replace(/\s+/g, " ");
    let cursor = 0;
    while (cursor < haystack.length) {
      const idx = haystack.indexOf(needle, cursor);
      if (idx < 0) break;
      out.push({
        messageId: row.id,
        role: row.role,
        timestamp: row.timestamp,
        excerpt: buildExcerpt(row.text, query),
      });
      cursor = idx + needle.length;
      if (out.length >= limit) break;
    }
  }
  return out;
}

// ------------------------------------------------------------------
// Public API
// ------------------------------------------------------------------

/**
 * Run the in-thread deep-batch search for the ACTIVE chat.
 *
 * Returns matches ordered: live matches first (in chat order),
 * then deep matches (oldest page first, within each page oldest
 * message first).
 *
 * Side effects:
 *   - Issues `chat.history` RPCs ONLY when the live cache has zero
 *     matches AND the server says older content exists. Otherwise
 *     the call is zero-RPC.
 *   - Never mutates `app.chatMessagesBySession`, `app.chatMessages`,
 *     or any other host state. The Ephemeral index in module scope
 *     is the only side effect.
 *   - Increments `app.chatMessageSearchTotal` and clears the cache
 *     for the old session on session switch (see
 *     `pruneDeepBatchCache` — wired by the host via the
 *     `onChatSessionChanged` event).
 *
 * Concurrency: a single in-flight walker. Subsequent calls while
 * the walker is running bump the generation counter and return
 * the partial result so far.
 */
export async function searchActiveChatDeepBatch(
  app: ActiveChatSource,
  query: string,
): Promise<DeepBatchSearchResult> {
  // JAISIU-3655 — gate on non-whitespace content (not the raw
  // length) so a query like "can " (trailing space) passes the
  // gate the same way "can" does, while a pure-whitespace query
  // still bails out. The raw query (with trailing whitespace if
  // any) is forwarded into the scan so the match is "can " not
  // "can" — see the operator's note that the search engine
  // should "take spaces into account".
  const sessionKey = (app.sessionKey ?? "").trim();
  const effectiveLength = query.replace(/\s+/g, "").length;
  const empty: DeepBatchSearchResult = {
    sessionKey,
    query,
    liveMatches: [],
    deepMatches: [],
    total: 0,
    exhausted: false,
  };
  if (!sessionKey) return empty;
  if (effectiveLength < MIN_QUERY_LENGTH) return empty;
  if (!app.connected || !app.client) return empty;

  // Phase 1 — live cache. Always zero RPCs. We forward the RAW
  // query (with any trailing / leading whitespace preserved) so
  // the search contract is consistent across every site — the
  // chat view filter, the highlight pass, and the deep walker all
  // agree that "can " is distinct from "can".
  const live = Array.isArray(app.chatMessages) ? app.chatMessages : [];
  const liveMatches = scanLiveForMatches(live, query, MAX_DEEP_RESULTS);

  // If the live cache produced any matches, return immediately.
  // Older pages are NOT paged — the operator is reading right now,
  // so a deep walk would only widen the result set with strictly
  // older context the operator can reach by scrolling up.
  if (liveMatches.length > 0) {
    return {
      sessionKey,
      query: query,
      liveMatches,
      deepMatches: [],
      total: liveMatches.length,
      exhausted: false,
    };
  }

  // Phase 2 — deep walker. Only when the live slice has NO matches
  // and the server still has older content to give us.
  if (!app.chatHistoryHasMoreOlder) {
    // Live cache is the full transcript for this chat. No older
    // history exists; report zero and stop.
    return {
      sessionKey,
      query: query,
      liveMatches,
      deepMatches: [],
      total: liveMatches.length,
      exhausted: true,
    };
  }

  const generation = (deepSearchGeneration += 1);
  if (deepSearchInFlight) {
    // Coalesce — a previous walker is still paging. We have already
    // collected whatever live matches exist; the next call will
    // collect whatever the in-flight walker has buffered. For now
    // return what we have so the UI can keep its counter accurate.
    return {
      sessionKey,
      query: query,
      liveMatches,
      deepMatches: [],
      total: liveMatches.length,
      exhausted: false,
    };
  }
  deepSearchInFlight = true;

  try {
    const entry: DeepSessionCacheEntry = deepCache.get(sessionKey) ?? {
      sessionKey,
      pages: [],
      exhausted: false,
      nextCursor: app.chatHistoryOldestLoadedId ?? null,
    };
    if (deepCache.get(sessionKey) !== entry) {
      deepCache.set(sessionKey, entry);
    }

    let cursor = entry.nextCursor ?? app.chatHistoryOldestLoadedId ?? null;
    let exhausted = entry.exhausted;

    for (let page = 0; page < MAX_PAGES; page += 1) {
      if (deepSearchGeneration !== generation) break;
      if (exhausted) break;
      if (app.chatHistoryLoadingOlder) break;
      if (!app.client) break;

      const params: Record<string, unknown> = {
        sessionKey,
        limit: PAGE_SIZE,
      };
      if (cursor) {
        params.before = cursor;
      }

      let response:
        | { messages?: unknown[]; oldestMessageId?: unknown; hasMoreOlder?: unknown }
        | undefined;
      try {
        response = await app.client.request<{
          messages?: unknown[];
          oldestMessageId?: unknown;
          hasMoreOlder?: unknown;
        }>("chat.history", params);
      } catch {
        // Best-effort. A single failed page aborts the walker but
        // does NOT throw — the counter still works from whatever
        // older pages we managed to walk before the failure.
        exhausted = true;
        break;
      }

      const built = buildPage(response ?? {});
      entry.pages.push(built);
      if (built.oldestMessageId) cursor = built.oldestMessageId;
      exhausted = !built.hasMoreOlder;
      if (built.rows.length === 0) break;
    }

    entry.exhausted = exhausted;
    entry.nextCursor = cursor;

    // Scan whatever pages we accumulated for matches.
    const allMatches: DeepMatch[] = [];
    // Pages are stored newest-first; we want older-first so the
    // counter advances toward older content as the operator presses ↓.
    for (let i = entry.pages.length - 1; i >= 0; i -= 1) {
      const page = entry.pages[i]!;
      const pageMatches = scanPageForMatches(page, query, MAX_DEEP_RESULTS - allMatches.length);
      for (const m of pageMatches) {
        if (allMatches.length >= MAX_DEEP_RESULTS) break;
        allMatches.push(m);
      }
      if (allMatches.length >= MAX_DEEP_RESULTS) break;
    }

    if (deepSearchGeneration !== generation) {
      // A newer search supersedes this one. Return what we have so
      // far rather than the stale state.
      return {
        sessionKey,
        query: query,
        liveMatches,
        deepMatches: [],
        total: liveMatches.length,
        exhausted,
      };
    }

    return {
      sessionKey,
      query: query,
      liveMatches,
      deepMatches: allMatches,
      total: liveMatches.length + allMatches.length,
      exhausted,
    };
  } finally {
    if (deepSearchGeneration === generation) {
      deepSearchInFlight = false;
    }
  }
}

/**
 * Bring a specific deep-batch match's page into the live transcript
 * so the operator's `scrollIntoView` lands on a real, rendered row.
 *
 * Pure side-effect wrapper around `loadOlderChatHistory` (host
 * method). Returns true when the messageId is now present in
 * `app.chatMessages`, false when the page could not be loaded (the
 * server reported no more content, or a per-call error fired).
 *
 * Critical invariant: this method is the ONLY caller in the
 * in-thread search path that mutates `chatMessagesBySession`. Every
 * other search path stays read-only — only a navigation event
 * triggers a prepend.
 */
export async function navigateToChatThreadDeepMatch(
  app: ActiveChatSource & { loadOlderChatHistory: () => Promise<void> },
  messageId: string,
): Promise<boolean> {
  const target = messageId.trim();
  if (!target) return false;
  const sessionKey = (app.sessionKey ?? "").trim();
  if (!sessionKey) return false;

  // Fast path — already in the live cache; nothing to do.
  const messages = Array.isArray(app.chatMessages) ? app.chatMessages : [];
  if (messages.some((m) => getMessageId(m) === target)) {
    return true;
  }

  // Walk older pages until the message id appears. `loadOlderChatHistory`
  // respects the host's `chatHistoryHasMoreOlder` cursor and its
  // in-flight guard, so we do not duplicate the cursor logic here —
  // we trust the host controller to stop on its own.
  let safety = 0;
  while (safety < MAX_PAGES) {
    safety += 1;
    if (!app.chatHistoryHasMoreOlder) return false;
    if (app.chatHistoryLoadingOlder) {
      // Another load is in flight — wait one tick, then re-check.
      await new Promise<void>((resolve) => setTimeout(resolve, 50));
      const refreshed = Array.isArray(app.chatMessages) ? app.chatMessages : [];
      if (refreshed.some((m) => getMessageId(m) === target)) return true;
      continue;
    }
    await app.loadOlderChatHistory();
    const updated = Array.isArray(app.chatMessages) ? app.chatMessages : [];
    if (updated.some((m) => getMessageId(m) === target)) return true;
  }
  return false;
}

/**
 * Drop the walker cache for a session. Called by the host on every
 * session switch so the ephemeral index never leaks across chats.
 * Exported for tests.
 */
export function clearActiveChatDeepSearch(sessionKey?: string): void {
  if (sessionKey) {
    deepCache.delete(sessionKey);
    return;
  }
  deepCache.clear();
  deepSearchGeneration += 1;
  deepSearchInFlight = false;
}

/** Read-only snapshot of the cache. Used by tests. */
export function inspectChatThreadSearchDeepCache(): ReadonlyMap<string, DeepSessionCacheEntry> {
  return deepCache;
}

/** True while a walker is currently paging older batches. */
export function isActiveChatDeepSearchInFlight(): boolean {
  return deepSearchInFlight;
}

/** True when the row's text contains the query (case-insensitive).
 * Exported for unit tests. */
export function deepRowMatchesQuery(row: DeepRow, query: string): boolean {
  if (!row.textLowercase) return false;
  return row.textLowercase.includes(query.toLowerCase());
}
